import { Flex, useDisclosure } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useDeleteRegistrar from './queries/useDeleteRegistrar';
import useGetRegistrar from './queries/useGetRegistrar';
import useUpdateRegistrar from './queries/useUpdateRegistrar';

import FormBase from '@/components/forms/new/FormBase';
import { Button, LoadingIndicator } from 'components/v4';
import {
    breadCrumbTrailEdit as breadCrumbTrail,
    editHeader as header,
    PROTOCOL,
    registrarsPath,
} from 'pages/resources/pbx/constants';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import PasswordField from './formFields/PasswordField';
import ProtocolField from './formFields/ProtocolField';
import ProxyField from './formFields/ProxyField';
import RegistrarNameField from './formFields/RegistrarNameField';
import RegistrationDurationField from './formFields/RegistrationDurationField';
import URIField from './formFields/URIField';
import UsernameField from './formFields/UsernameField';

// NOTE: registrar edit page is pbx for route convention
const RegistrarsEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const { restError } = useSelector((state) => state.modal);

    const [isDisabled, setIsDisabled] = useState(true);
    const [currentRegistrar, setCurrentRegistrar] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { filter = 'Company' } = useLocation()?.state || {};

    const statefulBreadCrumTrail = breadCrumbTrail.map(
        ({ label, path: prevPath }) => ({
            label,
            path: [prevPath, { index: prevPath ? 0 : null, filter }],
        }),
    );

    const {
        data: registrarData,
        isLoading: registrarLoading,
        error: registrarError,
        isFetching: registrarFetching,
    } = useGetRegistrar(id);

    useEffect(() => {
        if (registrarData) {
            setCurrentRegistrar(registrarData);
        }
    }, [registrarData]);

    const handleUpdateSuccess = () => {
        toast.success('Registrar updated successfully!');
        navigate(registrarsPath, {
            state: { filter },
        });
    };

    const handleUpdateError = (error) => {
        console.error('Failed to update registrar:', error);
        toast.error(
            restError ?? 'Failed to update registrar. Please try again.',
        );
    };

    const handleDeleteSuccess = () => {
        toast.success('Registrar deleted successfully!');
        navigate(registrarsPath, {
            state: { filter },
        });
        onClose();
    };

    const handleDeleteError = (error) => {
        console.error('Failed to delete registrar:', error);
        toast.error('Failed to delete registrar. Please try again.');
    };

    const { mutate: updateRegistrar, isPending: updatePending } =
        useUpdateRegistrar(handleUpdateSuccess, handleUpdateError);

    const { mutate: deleteRegistrar, isPending: deletePending } =
        useDeleteRegistrar(handleDeleteSuccess, handleDeleteError);

    const finalFormSubmit = async (values) => {
        if (!values.name || values.name.length < 2) {
            console.error(
                'Registrar name is required and must be at least 2 characters',
            );
            return;
        }

        if (!values.uri) {
            console.error('URI is required');
            return;
        }

        const isCompanyFilter = filter === 'Company';

        const finalSubmit = {
            id: id,
            name: values.name,
            uri: values.uri,
            protocol:
                values.protocol !== undefined
                    ? values.protocol
                    : PROTOCOL.DEFAULT, // Default to (0) if not set by user
            proxy: values.proxy || null,
            registrationDuration: parseInt(values.registrationDuration, 10),
            isPublic: isCompanyFilter ? false : true,
            username: values.username,
            changePassword:
                values.password !== currentRegistrar?.password ? true : false,
            password: values.password,
        };

        updateRegistrar(finalSubmit);
    };

    const handleDeleteConfirm = () => {
        deleteRegistrar(id);
    };

    const formFields = (errors, touched, values) => {
        const fields = [
            <RegistrarNameField
                key="name"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <URIField
                key="uri"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <ProxyField
                key="proxy"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <ProtocolField
                key="protocol"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <RegistrationDurationField
                key="registrationDuration"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <UsernameField
                key="username"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <PasswordField
                key="password"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
        ];

        const hasRequiredFields = values?.name && values?.uri;

        if (hasRequiredFields) {
            setIsDisabled(false);
        }

        return fields;
    };

    const extraButton = (
        <Flex justify="flex-end" width="100%">
            <Button
                w={142}
                colorScheme="red"
                onClick={onOpen}
                isDisabled={deletePending || updatePending}>
                Delete
            </Button>
        </Flex>
    );

    const isFetchingData =
        registrarLoading || registrarFetching || updatePending;

    if (registrarError) {
        toast.error(
            'Failed to load registrar data. Redirecting to registrars list.',
        );
        navigate(registrarsPath);
        return <LoadingIndicator />;
    }

    if (isFetchingData || !currentRegistrar) {
        return <LoadingIndicator />;
    }

    // Prepare initial values with fetched registrar data
    const editInitialValues = {
        name: currentRegistrar.name || '',
        regionID: currentRegistrar.region?.id || '',
        protocol:
            currentRegistrar.protocol !== undefined
                ? currentRegistrar.protocol
                : PROTOCOL.DEFAULT,
        uri: currentRegistrar.uri || '',
        proxy: currentRegistrar.proxy || '',
        registrationDuration:
            currentRegistrar.registrationDuration?.toString() || '3600', // Default to 3600 if not set
        username: currentRegistrar.username || '',
        password:
            !currentRegistrar?.username &&
            currentRegistrar.password === '********' // if no username and password, don't show password
                ? ''
                : currentRegistrar.password || '',
    };

    return (
        <>
            <FormBase
                isDisabled={isDisabled}
                breadCrumbTrail={statefulBreadCrumTrail}
                header={header}
                initialValues={editInitialValues}
                finalFormSubmit={finalFormSubmit}
                backLink={[registrarsPath, { state: { index: 0, filter } }]}
                extraButton={extraButton}>
                {({ errors, touched, values }) =>
                    formFields(errors, touched, values)
                }
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isOpen}
                onClose={onClose}
                registrarName={currentRegistrar?.name}
                onConfirm={handleDeleteConfirm}
                isDeleting={deletePending}
            />
        </>
    );
};

export default RegistrarsEdit;
